import { ShopifyFinance, ShopifyHistoryNotice, useCommerceOrders } from '../components/ShopifyHistory';
// ===========================================
// P&L — CPG VIEW  (route /pnl, CPG accounts)
// ===========================================
// A monthly build-down from gross revenue to gross profit, computed client-side
// from the same wholesale summaries the Channels tab reads:
//   Gross revenue by sales category    /wholesale/summary/categories
//   − Distributor deductions           /wholesale/deductions + /deductions/summary
//   = Net revenue
//   − Cost of goods (cost per case)    categories summary (blank until costs exist)
//   − Delivery & fulfillment           categories summary (+ delivery summary context)
//   = Gross profit + margin %
// Operating expenses sit below: with QuickBooks connected they reuse the same
// statement engine the store P&L reads (supplier invoices + manual lines);
// without it, an honest pointer to Integrations — never a placeholder number.
// Months come straight from the categories summary. Any entity/region params
// that response may carry in the future are deliberately ignored here — no
// entity toggle on this page yet.
//
// Around the statement, the analysis layer (all client-side, all template
// sentences over the same payloads — see lib/pnlAnalysis):
//   · a ratio strip up top, each account number against a curated
//     rule-of-thumb band (lib/pnlBenchmarks — clearly captioned, never data)
//   · month-over-month deltas on every money line + a 3/6/all period view
//   · channel economics per sales category, with computed commentary
//   · "What the numbers say" — observations only when the inputs exist

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Printer, Plug, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { wholesaleApi, pnlApi } from '../services/api';
import { fmtMoneyShort } from '../utils/cpg';
import { buildPnlAnalysis } from '../lib/pnlAnalysis';
import RatioStrip from '../components/pnl/RatioStrip';
import ChannelEconomics from '../components/pnl/ChannelEconomics';
import InsightsPanel from '../components/pnl/InsightsPanel';

// ---------- types (mirror the API responses) ----------
interface MonthlyCell { month: string; revenueCents: number; marginCents: number | null }
interface CategoryRow {
  category: string; label: string; customers: number; orders: number;
  revenueCents: number; cogsCents: number | null; deliveryCents: number;
  marginCents: number | null; marginPct: number | null; monthly: MonthlyCell[];
}
interface CategorySummary { months: string[]; rows: CategoryRow[]; hasCosts: boolean; source: string }
interface PnlLine { category: string; label: string; amountCents: number; sourceNote: string }
interface PnlStatement { scope: { start: string; end: string }; opex: PnlLine[]; opexTotalCents: number }

// P&L order + labels for the build-down (the biggest channel first; the API's
// short "Distribution" label reads better in full on a statement).
const PNL_CATEGORY_ORDER = ['distribution', 'food_service', 'retail', 'ecommerce'];
const PNL_CATEGORY_LABELS: Record<string, string> = {
  distribution: 'Wholesale & distribution', food_service: 'Food service', retail: 'Retail', ecommerce: 'E-commerce',
};
const DEDUCTION_REASON_LABELS: Record<string, string> = {
  promo: 'Promo', advertising: 'Co-op advertising', slotting: 'Slotting', samples: 'Samples', catalog: 'Catalog fee',
  chargeback: 'Chargeback (MCB)', freight: 'Freight', shortpay: 'Short-pay', out_of_code: 'Out of code', spoilage: 'Spoilage', other: 'Unexplained',
};

// −$1,234 for outflows; whole dollars like the rest of the CPG pages.
const negMoney = (c: number) => (c === 0 ? fmtMoneyShort(0) : `−${fmtMoneyShort(Math.abs(c))}`);
// Sign-aware: a month's net revenue can dip below zero when a dated deduction
// lands in a thin month — render it as −$x, never "$-x".
const signedMoney = (c: number) => (c < 0 ? negMoney(c) : fmtMoneyShort(c));
const Dash = () => <span className="text-slate-300">—</span>;

// Month-over-month delta under a money cell — only when both months carry a
// positive figure, so a blank month never pretends to be a −100% collapse.
function Delta({ cur, prev }: { cur: number | null | undefined; prev: number | null | undefined }) {
  if (!cur || !prev || cur <= 0 || prev <= 0) return null;
  const pct = Math.round(((cur - prev) / prev) * 100);
  return (
    <span className="block text-[10px] leading-tight text-slate-400 font-normal">
      {pct === 0 ? '±0%' : `${pct > 0 ? '+' : '−'}${Math.abs(pct)}%`}
    </span>
  );
}

function getCurrentRestaurantId(): string | null {
  try { return JSON.parse(localStorage.getItem('manager_current_restaurant') || 'null')?.id || null; } catch { return null; }
}
const localIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export default function PnlCpgPage() {
  const {data:orders=[], isPending, isError} = useCommerceOrders();
  if (isPending) return <div className="p-6 text-slate-500">Loading sales history…</div>;
  if (isError) return <div role="alert" className="p-6 text-slate-500">Sales history could not be loaded. Please refresh.</div>;
  if (orders.some(o => o.source === 'shopify')) return <div className="p-4 md:p-6 space-y-6"><div><h1 className="text-xl md:text-2xl font-bold">P&amp;L</h1><p className="text-slate-500 text-sm mt-1">Sales history and accounting coverage</p></div><ShopifyHistoryNotice/><ShopifyFinance orders={orders}/>{orders.some(o=>o.source !== 'shopify') && <p className="text-sm text-slate-500">This section covers Shopify orders. Other orders remain available in Wholesale.</p>}</div>;
  return <LegacyPnlCpgPage />;
}
function LegacyPnlCpgPage() {
  // Same query keys the Channels components use, so caches stay shared.
  const { data: cats, isPending: catsPending } = useQuery<CategorySummary | null>({
    queryKey: ['wholesale-category-summary'],
    queryFn: async () => (await wholesaleApi.getCategorySummary()).data?.data || null,
    staleTime: 60_000,
  });
  const { data: dedSummary } = useQuery({
    queryKey: ['wholesale-deduction-summary'],
    queryFn: () => wholesaleApi.getDeductionSummary().then((r) => r.data.data),
  });
  const { data: deductions = [] } = useQuery({
    queryKey: ['wholesale-deductions'],
    queryFn: () => wholesaleApi.getDeductions().then((r) => r.data.data as any[]),
  });
  const { data: deliverySummary } = useQuery({
    queryKey: ['wholesale-delivery-summary'],
    queryFn: async () => (await wholesaleApi.getDeliverySummary()).data?.data || null,
    staleTime: 60_000,
  });
  const { data: qbo } = useQuery({
    queryKey: ['quickbooks-status'],
    queryFn: async () => (await wholesaleApi.getQuickbooksStatus()).data?.data || { connected: false },
  });
  // Invoices feed days-to-pay and the aging cash note; customers map each
  // deduction and drop to its channel. Query keys shared with Accounts/Channels.
  const { data: invoices = [] } = useQuery({
    queryKey: ['wholesale-invoices'],
    queryFn: async () => ((await wholesaleApi.getInvoices()).data?.data || []) as any[],
  });
  const { data: customers = [] } = useQuery({
    queryKey: ['wholesale-customers'],
    queryFn: async () => ((await wholesaleApi.getCustomers()).data?.data || []) as any[],
  });

  const months: string[] = cats?.months || [];

  // How many monthly columns to show — the analysis always reads every month,
  // and the Total column always covers everything recorded.
  const [span, setSpan] = useState<'3' | '6' | 'all'>('3');
  const shownMonths = span === 'all' ? months : months.slice(-Number(span));
  const offset = months.length - shownMonths.length;

  // Operating expenses, only once QuickBooks is connected: the same statement
  // engine the store P&L reads (supplier invoices grouped by vendor category,
  // plus manual lines), over the months the build-down shows.
  const restaurantId = getCurrentRestaurantId() || 'all';
  const endIso = localIso(new Date());
  const { data: statement, isPending: statementPending } = useQuery<PnlStatement>({
    queryKey: ['pnl-statement-cpg', restaurantId, months[0], endIso],
    enabled: qbo?.connected === true && months.length > 0,
    queryFn: async () => {
      const res = await pnlApi.getStatement({ restaurantId, start: `${months[0]}-01`, end: endIso });
      return res.data?.data || res.data;
    },
  });

  // ---------- the build-down, computed from the fetched summaries ----------
  const model = useMemo(() => {
    if (!cats) return null;
    const byCat = new Map(cats.rows.map((r) => [r.category, r]));
    const catRows = PNL_CATEGORY_ORDER
      .map((c) => byCat.get(c))
      .filter((r): r is CategoryRow => !!r && (r.revenueCents > 0 || r.orders > 0))
      .map((r) => ({ ...r, label: PNL_CATEGORY_LABELS[r.category] || r.label }));
    const uncat = byCat.get('uncategorized');
    if (uncat && (uncat.revenueCents > 0 || uncat.orders > 0)) catRows.push(uncat);

    const grossByMonth = months.map((_, i) => catRows.reduce((s, r) => s + (r.monthly[i]?.revenueCents || 0), 0));
    const grossTotal = catRows.reduce((s, r) => s + r.revenueCents, 0);

    // Deductions: the total and rate come from the reconciliation summary; the
    // monthly split comes from the ledger itself — each dated deduction lands
    // in its month. Undated or out-of-range ones count in the Total column only.
    const dedTotal: number = dedSummary?.totals?.deductionCents || 0;
    const rateBps: number | null = dedSummary?.totals?.rateBps ?? null;
    const monthIndex = new Map(months.map((m, i) => [m, i]));
    const dedByMonth = months.map(() => 0);
    let dedDatedInRange = 0;
    for (const d of deductions) {
      const m = d.deductionDate ? new Date(d.deductionDate).toISOString().slice(0, 7) : null;
      const i = m != null ? monthIndex.get(m) : undefined;
      if (i !== undefined) { dedByMonth[i] += d.amountCents || 0; dedDatedInRange += d.amountCents || 0; }
    }
    const dedRemainder = dedTotal - dedDatedInRange; // undated or outside the shown months
    const topReasons: Array<{ label: string; amountCents: number }> = (dedSummary?.byReason || [])
      .slice(0, 3)
      .map((r: any) => ({ label: DEDUCTION_REASON_LABELS[r.reasonCode] || r.reasonCode, amountCents: r.amountCents }));

    const netByMonth = months.map((_, i) => grossByMonth[i] - dedByMonth[i]);
    const netTotal = grossTotal - dedTotal;

    const hasCosts = !!cats.hasCosts;
    const cogsTotal = hasCosts ? catRows.reduce((s, r) => s + (r.cogsCents || 0), 0) : null;
    const deliveryTotal = catRows.reduce((s, r) => s + (r.deliveryCents || 0), 0);
    const dropsRecorded: number = deliverySummary?.totals?.drops ?? 0;

    // Gross profit per month: the summary's monthly margin (revenue − cost per
    // case − delivery, computed order by order server-side) minus that month's
    // deductions. The COGS/delivery monthly split isn't published separately,
    // so those two rows carry their Total only — never a spread we invented.
    const gpByMonth = hasCosts
      ? months.map((_, i) => catRows.reduce((s, r) => s + (r.monthly[i]?.marginCents ?? 0), 0) - dedByMonth[i])
      : null;
    const gpTotal = hasCosts && cogsTotal != null ? netTotal - cogsTotal - deliveryTotal : null;
    const gpPctByMonth = gpByMonth ? months.map((_, i) => (netByMonth[i] > 0 ? Math.round((gpByMonth[i] / netByMonth[i]) * 100) : null)) : null;
    const gpPctTotal = gpTotal != null && netTotal > 0 ? Math.round((gpTotal / netTotal) * 100) : null;

    return {
      catRows, grossByMonth, grossTotal,
      dedByMonth, dedTotal, dedRemainder, dedDatedInRange, rateBps, topReasons,
      netByMonth, netTotal,
      hasCosts, cogsTotal, deliveryTotal, dropsRecorded,
      gpByMonth, gpTotal, gpPctByMonth, gpPctTotal,
    };
  }, [cats, months, dedSummary, deductions, deliverySummary]);

  // The analysis layer: ratios, channel economics and computed observations —
  // pure functions over the same payloads, in lib/pnlAnalysis.
  const analysis = useMemo(
    () => buildPnlAnalysis({
      cats: cats || null, dedSummary, deductions, deliverySummary, invoices, customers,
      categoryLabels: PNL_CATEGORY_LABELS,
    }),
    [cats, dedSummary, deductions, deliverySummary, invoices, customers],
  );

  // Month headers: "Mar", with the year added when the range spans two.
  const spansYears = new Set(shownMonths.map((m) => m.slice(0, 4))).size > 1;
  const monthLabel = (ym: string) => {
    const [y, m] = ym.split('-').map(Number);
    const name = new Date(y, (m || 1) - 1, 1).toLocaleDateString('en-US', { month: 'short' });
    return spansYears ? `${name} ’${String(y).slice(2)}` : name;
  };

  if (catsPending) {
    return (
      <div className="p-4 md:p-6">
        <div className="card p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-600" /><p className="mt-4 text-slate-500">Building the statement…</p></div>
      </div>
    );
  }

  const empty = !model || months.length === 0 || model.catRows.length === 0;

  return (
    <div className="p-4 md:p-6 space-y-5 print:p-0">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .pnl-sheet { box-shadow: none !important; border: none !important; }
          .card { box-shadow: none !important; break-inside: avoid; }
          @page { margin: 14mm; }
        }
      `}</style>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">P&amp;L</h1>
          <p className="text-slate-500 text-sm">Monthly build-down from gross revenue to gross profit.</p>
          <p className="text-xs text-slate-400 mt-0.5">
            Revenue, deductions and costs are computed from the orders, invoices and payments recorded in Mise;
            operating costs fill in below once QuickBooks is connected. Nothing here is estimated.
          </p>
        </div>
        {!empty && (
          <div className="no-print flex items-center gap-2">
            {months.length > 3 && (
              <div className="flex gap-1">
                {(['3', '6', 'all'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSpan(s)}
                    className={clsx(
                      'px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                      span === s ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
                    )}
                  >
                    {s === 'all' ? `All ${months.length} mo` : `${s} mo`}
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => window.print()} className="btn btn-secondary inline-flex items-center gap-2">
              <Printer className="w-4 h-4" /> Print / PDF
            </button>
          </div>
        )}
      </div>

      {empty ? (
        <div className="card p-10 text-center text-slate-500">
          No counted wholesale orders yet. The P&amp;L builds itself from confirmed, fulfilled and invoiced orders —
          once the first order confirms, gross revenue, deductions and costs land here month by month.
        </div>
      ) : (
        <>
          {/* Ratio strip — the account's numbers against curated reference bands */}
          {analysis && <RatioStrip a={analysis} />}

          {/* The build-down table */}
          <div className="pnl-sheet card p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-slate-400 border-b border-slate-100">
                  <th className="px-4 py-2.5 font-medium text-left min-w-[220px]">Line</th>
                  {shownMonths.map((m) => <th key={m} className="px-3 py-2.5 font-medium text-right whitespace-nowrap">{monthLabel(m)}</th>)}
                  <th className="px-4 py-2.5 font-medium text-right whitespace-nowrap border-l border-slate-200">Total</th>
                </tr>
              </thead>
              <tbody>
                {/* 1 · Gross revenue by sales category */}
                <SectionRow cols={shownMonths.length} title="Gross revenue — by sales category" />
                {model.catRows.map((r) => (
                  <tr key={r.category} className="border-b border-slate-50">
                    <td className="px-4 py-2 pl-7 text-slate-700 whitespace-nowrap">
                      {r.label}
                      {r.category === 'uncategorized' && <span className="block text-[11px] text-slate-400">set each customer’s category on Wholesale → Customers</span>}
                    </td>
                    {shownMonths.map((m, j) => {
                      const i = j + offset;
                      return (
                        <td key={m} className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-slate-600">
                          {r.monthly[i]?.revenueCents ? (
                            <>
                              {fmtMoneyShort(r.monthly[i].revenueCents)}
                              <Delta cur={r.monthly[i]?.revenueCents} prev={r.monthly[i - 1]?.revenueCents} />
                            </>
                          ) : <Dash />}
                        </td>
                      );
                    })}
                    <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap font-medium text-slate-900 border-l border-slate-200">{fmtMoneyShort(r.revenueCents)}</td>
                  </tr>
                ))}
                <TotalsRow
                  label="Gross revenue"
                  byMonth={shownMonths.map((_, j) => {
                    const i = j + offset;
                    return <>{fmtMoneyShort(model.grossByMonth[i])}<Delta cur={model.grossByMonth[i]} prev={model.grossByMonth[i - 1]} /></>;
                  })}
                  total={fmtMoneyShort(model.grossTotal)}
                />

                {/* 2 · Deductions */}
                <tr className="border-b border-slate-50">
                  <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
                    Less: distributor deductions
                    {model.dedTotal > 0 && model.rateBps != null && (
                      <span className="block text-[11px] text-slate-400">{(model.rateBps / 100).toFixed(1)}% of revenue on the deductions ledger</span>
                    )}
                    {model.dedTotal === 0 && (
                      <span className="block text-[11px] text-slate-400">none recorded yet — log them on Wholesale → Deductions</span>
                    )}
                  </td>
                  {shownMonths.map((m, j) => {
                    const i = j + offset;
                    return (
                      <td key={m} className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-slate-600">
                        {model.dedByMonth[i] ? (
                          <>
                            {negMoney(model.dedByMonth[i])}
                            <Delta cur={model.dedByMonth[i]} prev={model.dedByMonth[i - 1]} />
                          </>
                        ) : <Dash />}
                      </td>
                    );
                  })}
                  <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap font-medium border-l border-slate-200">
                    {model.dedTotal ? <span className="text-slate-900">{negMoney(model.dedTotal)}</span> : <Dash />}
                  </td>
                </tr>
                <TotalsRow
                  label="Net revenue"
                  byMonth={shownMonths.map((_, j) => {
                    const i = j + offset;
                    return <>{signedMoney(model.netByMonth[i])}<Delta cur={model.netByMonth[i]} prev={model.netByMonth[i - 1]} /></>;
                  })}
                  total={signedMoney(model.netTotal)}
                />

                {/* 3 · Costs: tracked per order; the monthly split folds into gross profit */}
                <SectionRow cols={shownMonths.length} title="Cost of goods & fulfillment" />
                <tr className="border-b border-slate-50">
                  <td className="px-4 py-2 pl-7 text-slate-700 whitespace-nowrap">
                    Less: cost of goods
                    {!model.hasCosts && <span className="block text-[11px] text-slate-400">blank until you enter cost per case on Wholesale → Channels — nothing is estimated</span>}
                  </td>
                  {shownMonths.map((m) => <td key={m} className="px-3 py-2 text-right"><Dash /></td>)}
                  <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap font-medium border-l border-slate-200">
                    {model.cogsTotal != null ? <span className="text-slate-900">{negMoney(model.cogsTotal)}</span> : <Dash />}
                  </td>
                </tr>
                <tr className="border-b border-slate-50">
                  <td className="px-4 py-2 pl-7 text-slate-700 whitespace-nowrap">
                    Less: delivery &amp; fulfillment
                    {model.deliveryTotal === 0 && model.dropsRecorded === 0 && (
                      <span className="block text-[11px] text-slate-400">no delivery costs recorded on orders yet</span>
                    )}
                  </td>
                  {shownMonths.map((m) => <td key={m} className="px-3 py-2 text-right"><Dash /></td>)}
                  <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap font-medium border-l border-slate-200">
                    {model.deliveryTotal > 0 || model.dropsRecorded > 0 ? <span className="text-slate-900">{negMoney(model.deliveryTotal)}</span> : <Dash />}
                  </td>
                </tr>

                {/* 4 · Gross profit + margin */}
                <tr className="border-t-2 border-slate-900">
                  <td className="px-4 py-2.5 font-semibold text-slate-900 whitespace-nowrap">Gross profit</td>
                  {shownMonths.map((m, j) => {
                    const i = j + offset;
                    return (
                      <td key={m} className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap font-semibold">
                        {model.gpByMonth ? (
                          <>
                            <span className={clsx(model.gpByMonth[i] < 0 && 'text-red-600')}>{model.gpByMonth[i] < 0 ? negMoney(model.gpByMonth[i]) : fmtMoneyShort(model.gpByMonth[i])}</span>
                            <Delta cur={model.gpByMonth[i]} prev={model.gpByMonth[i - 1]} />
                          </>
                        ) : <Dash />}
                      </td>
                    );
                  })}
                  <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap font-bold border-l border-slate-200">
                    {model.gpTotal != null ? (
                      <span className={clsx(model.gpTotal < 0 && 'text-red-600')}>{model.gpTotal < 0 ? negMoney(model.gpTotal) : fmtMoneyShort(model.gpTotal)}</span>
                    ) : <Dash />}
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2 text-slate-500 whitespace-nowrap">Gross margin</td>
                  {shownMonths.map((m, j) => {
                    const i = j + offset;
                    return (
                      <td key={m} className="px-3 py-2 text-right whitespace-nowrap">
                        {model.gpPctByMonth && model.gpPctByMonth[i] != null ? <MarginPct pct={model.gpPctByMonth[i]!} /> : <Dash />}
                      </td>
                    );
                  })}
                  <td className="px-4 py-2 text-right whitespace-nowrap border-l border-slate-200">
                    {model.gpPctTotal != null ? <MarginPct pct={model.gpPctTotal} /> : <Dash />}
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="text-[11px] text-slate-400 px-4 py-2.5 border-t border-slate-50">{cats?.source}</p>
          </div>

          {/* Honest footnotes — why a cell is a dash, never a guess */}
          <div className="text-xs text-slate-400 space-y-1">
            {shownMonths.length >= 2 && (
              <p>The small percentages under the monthly figures compare each month with the one before it.</p>
            )}
            {shownMonths.length < months.length && (
              <p>Showing the last {shownMonths.length} of {months.length} recorded months — the Total column always covers everything recorded, not just the months shown.</p>
            )}
            {model.dedTotal > 0 && model.dedDatedInRange === 0 && (
              <p>Deductions shown in total — the monthly split arrives as they accrue (give each deduction a date on Wholesale → Deductions and it lands in its month).</p>
            )}
            {model.dedDatedInRange !== 0 && model.dedRemainder !== 0 && (
              <p>{fmtMoneyShort(Math.abs(model.dedRemainder))} of deductions carry no date or fall outside these months — they count in the Total column only, so the monthly cells won’t sum to it.</p>
            )}
            {model.topReasons.length > 0 && (
              <p>Largest deduction reasons: {model.topReasons.map((r) => `${r.label} ${fmtMoneyShort(r.amountCents)}`).join(', ')} — the full ledger lives on Wholesale → Deductions.</p>
            )}
            {model.hasCosts && (
              <p>Cost of goods and delivery are tracked per order; the Total column carries each in full, and the gross-profit line reflects them month by month.</p>
            )}
          </div>

          {/* What the numbers say — computed observations, template-only */}
          {analysis && <InsightsPanel a={analysis} />}

          {/* Channel economics — per sales category, with computed commentary */}
          {analysis && <ChannelEconomics a={analysis} hasCosts={!!cats?.hasCosts} />}
        </>
      )}

      {/* Operating expenses — QuickBooks-gated, below the build-down */}
      {!empty && (
        qbo?.connected ? (
          <div className="card max-w-full overflow-hidden">
            <div className="px-5 pt-5 pb-3 border-b border-slate-100">
              <h2 className="font-semibold text-slate-900">Operating expenses</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                QuickBooks is connected. These lines come from the supplier invoices and manual entries recorded in Mise —
                the same statement engine the store P&amp;L reads — covering {months[0]}-01 → today.
              </p>
            </div>
            {statementPending ? (
              <p className="text-sm text-slate-500 px-5 py-6">Reading the expense lines…</p>
            ) : (() => {
              const lines = (statement?.opex || []).filter((l) => l.amountCents !== 0);
              if (lines.length === 0) {
                return (
                  <p className="text-sm text-slate-500 px-5 py-6">
                    No operating costs recorded for these months yet — rent, payroll and the rest appear here as
                    supplier invoices and manual lines land in Mise.
                  </p>
                );
              }
              return (
                <div className="px-5 py-3">
                  {lines.map((l) => (
                    <div key={l.category} className="flex items-center justify-between py-1.5">
                      <div className="min-w-0">
                        <span className="text-sm text-slate-700">{l.label}</span>
                        <span className="block text-xs text-slate-400">{l.sourceNote}</span>
                      </div>
                      <span className="text-sm tabular-nums text-slate-900">{fmtMoneyShort(l.amountCents)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between py-2 border-t border-slate-200 mt-1">
                    <span className="text-sm font-medium text-slate-600">Total operating expenses</span>
                    <span className="text-sm font-semibold tabular-nums">{fmtMoneyShort(statement?.opexTotalCents || 0)}</span>
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="no-print card p-5 flex items-start gap-3">
            <Plug className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-slate-900">Operating costs come from QuickBooks</p>
              <p className="text-sm text-slate-500 mt-0.5">
                Connect it and rent, payroll and the rest fill in here — until then the statement stops at gross profit.
                Nothing is estimated in the meantime.
              </p>
              <Link to="/settings?tab=integrations" className="btn btn-secondary mt-3 inline-flex items-center gap-2 text-sm">
                Open integrations
              </Link>
            </div>
          </div>
        )
      )}
    </div>
  );
}

// ---------- table sub-components ----------

// Tiny uppercase group header spanning the whole table, like the store P&L's.
function SectionRow({ title, cols }: { title: string; cols: number }) {
  return (
    <tr>
      <td colSpan={cols + 2} className="px-4 pt-3 pb-1 text-[11px] font-semibold text-slate-400 uppercase tracking-[0.08em]">{title}</td>
    </tr>
  );
}

// A bold subtotal line (Gross revenue, Net revenue). Cells arrive rendered so
// a month can carry its MoM delta under the figure.
function TotalsRow({ label, byMonth, total }: { label: string; byMonth: ReactNode[]; total: string }) {
  return (
    <tr className="border-t border-slate-200 border-b border-b-slate-50 bg-slate-50/40">
      <td className="px-4 py-2 font-semibold text-slate-900 whitespace-nowrap">{label}</td>
      {byMonth.map((v, i) => <td key={i} className="px-3 py-2 text-right tabular-nums whitespace-nowrap font-semibold text-slate-900">{v}</td>)}
      <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap font-bold text-slate-900 border-l border-slate-200">{total}</td>
    </tr>
  );
}

// Margin %, color-banded the same way ProductsPage bands product margins.
function MarginPct({ pct }: { pct: number }) {
  return (
    <span className={clsx('font-medium', pct < 20 ? 'text-red-600' : pct < 40 ? 'text-amber-600' : 'text-emerald-700')}>{pct}%</span>
  );
}
